import { Box, IconButton, Link, Stack, TextField, InputAdornment, Tooltip, Typography, Button } from "@mui/material";
import {
  Search as SearchIcon,
  TableChartOutlined as TableChartOutlinedIcon,
  PictureAsPdfOutlined as PictureAsPdfOutlinedIcon,
  RemoveCircleOutline as ExcludeIcon,
  RestoreFromTrash as RestoreIcon,
  GroupOutlined as GroupIcon,
} from "@mui/icons-material";
import { ReactNode, useMemo, useState } from "react";
import { CompactTable, CompactTableColumn } from "@/components/common/CompactTable";
import { gridRowId } from "../../utils/recordIndexing";
import { ValidationErrorsCell } from "./ReviewChips";
import { ForecastChip } from "./ForecastChip";
import type { IncomingClaimLine } from "@/resources/stoploss-claims/components/StoplossClaimPreviewDialog";
import { YTDForecastCell } from "./YTDForecastCell";
import type { YtdMismatch } from "../../utils/ytdReconciliation";
import { ClaimantNameCell, DeductibleCell, DeductibleHeader, YTDHeader, overDeductibleNote } from "./claimantGridCells";
import { sourceLabelFor } from "./ClaimLinesPanel";
import { ReimbursementForecastChip } from "./ReimbursementForecastChip";
import { ReimbursementsForecastCell } from "./ReimbursementsForecastCell";
import { ReimbursementRowStateChip } from "./ReimbursementRowStateChip";
import { ClaimItemRowStateChip } from "./ClaimItemRowStateChip";
import { ClaimLineOutcomeCell } from "./claimLineGridCells";
import { outsidePolicyLineFor } from "../../utils/outsidePolicy";
import { parseMoneyCell } from "../../utils/amounts";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import {
  ClaimantProjection,
  ClaimItemRowForecast,
  DocumentRecordType,
  ExtractedRecord,
  ReimbursementRowForecast,
  ReimbursementSummary,
  TargetSchema,
  ValidationError,
  type PolicyWindows,
} from "@/types";
import { getRecordTransformationErrors, getRecordValidationErrors, getRecordData } from "../../utils/recordUtils";

// ─── Row identity ───────────────────────────────────────────────────────────
//
// Delegated to gridRowId, which is SHARED with ExpandedRecordDetails'
// nested table. Both stamp the same record objects via Object.assign, and two
// independent id assigners meant the nested table silently overwrote this
// one's id — see gridRowId for the full mechanism and the symptom it caused.

type ExtractedRecordRow = ExtractedRecord & { id: string };

const asRow = (record: ExtractedRecord): ExtractedRecordRow =>
  // Safe to mutate: ExtractedRecord has no `id` field of its own, and the id
  // is now deterministic, so a re-stamp by either table is a no-op.
  Object.assign(record, { id: gridRowId(record) }) as ExtractedRecordRow;

// ─── Props ──────────────────────────────────────────────────────────────────

interface RecordReviewGridProps {
  /** Array of extracted records to display */
  records: ExtractedRecord[];
  /** Schema type for the records being displayed */
  targetSchema: TargetSchema;
  /** Column definitions (typically from buildDynamicColumns). */
  columns: CompactTableColumn<ExtractedRecord>[];
  /** Message to show when no records match filters */
  emptyMessage?: string;
  /** Callback when "View Source" is clicked on a record */
  onViewSource?: (record: ExtractedRecord) => void;
  /** Icon variant for the View Source button */
  sourceType?: "spreadsheet" | "pdf";
  /**
   * Total records stored for the document, when it is larger than what is
   * loaded (#6755).
   *
   * Search is a client-side match over the records IN HAND. On a paged
   * document that is a fraction of the document, and a search that finds
   * nothing looks identical to "this claimant is not in the file" — a false
   * negative on a claims review surface. These two props let the grid say what
   * it actually searched.
   */
  totalRecordCount?: number;
  /** True when more records exist than are loaded. */
  hasMoreRecords?: boolean;
  /**
   * Whether the reviewer has excluded a record.
   *
   * A predicate rather than a `Set<ExtractedRecord>` (#6755): membership by
   * object identity breaks the moment records are re-fetched or windowed into
   * fresh objects, silently dropping every exclusion. The owner keys its state
   * on the absolute record_index and answers through this.
   */
  isExcluded?: (record: ExtractedRecord) => boolean;
  /** Batch callbacks — receive the full list of selected records for cascade handling */
  onBulkExclude?: (records: ExtractedRecord[]) => void;
  onBulkRestore?: (records: ExtractedRecord[]) => void;
  /** Optional expand-panel renderer (used for top-row schemas to show linked records) */
  expandRow?: (record: ExtractedRecord) => ReactNode;
  /** Optional badge next to the expand chevron (e.g. linked-item count) */
  expandLabel?: (record: ExtractedRecord) => ReactNode;
  /** Optional predicate: when returns false, the expand chevron is suppressed
   * for that row. Used to hide the chevron when the expand panel would have
   * nothing meaningful to show. */
  isExpandable?: (record: ExtractedRecord) => boolean;
  /** When set, the Status column renders a forecast chip (match status + SL claim
   * prediction) for each record instead of the default ValidationErrorsCell.
   * Errors still take precedence — ForecastChip falls back to the error display.
   * A new "YTD before/after" column renders alongside. Only meaningful on top-row
   * schemas (claimants). */
  forecastGetter?: (record: ExtractedRecord) => ClaimantProjection | undefined;
  /** True while the forecast is being fetched — used to render a "Computing…" state. */
  isProjectionLoading?: boolean;
  /** When true, the policy has an active family-deductible endorsement; the
   * Family column renders to label subscribers/dependents/spouses. When false,
   * the column is suppressed entirely — there's no family unit on an
   * individual-deductible policy, so the column would just be blank noise. */
  isFamilyDeductiblePolicy?: boolean;
  /**
   * Claimant-grid context for the redesigned columns (#7435, artboard 2a).
   * When present on the SL top-row path the grid renders Claimant (with a
   * relationship sub-line), a single YTD, Deductible, and On approve, drops
   * the Member ID column (hover title + searchable instead), and the legacy
   * Family column.
   */
  claimantGrid?: {
    /** Linked claim-line count per claimant, for the sub-line. */
    lineCount?: (record: ExtractedRecord) => number;
    /** True on a paged document: the count covers loaded records only → "N+". */
    lineCountPartial?: boolean;
    /** "Sep 30, 2026" — shown under the YTD header. */
    asOfLabel?: string;
    /** Carrier + year for the Deductible header tooltip. */
    policyLabel?: string;
    /** Reported-YTD vs claim-lines gap per row (#7432 AC5); undefined when the row matches. */
    ytdMismatchFor?: (record: ExtractedRecord) => YtdMismatch | undefined;
    /** This document's claim lines for the row that will insert on approve — the open-claim preview's incoming rows. */
    incomingLinesFor?: (record: ExtractedRecord) => IncomingClaimLine[];
  };
  /**
   * Claim lines tab in the redesign's shape (artboard 2a): Claimant · Service
   * date · Paid date · Procedure · Paid · Source · On approve, replacing the
   * dynamic field columns (which stay behind "Show all columns"). Set only on
   * the CLAIM_ITEM tab of a claims document.
   */
  claimLinesGrid?: {
    /** Claimant name for a line; falls back to the record's own claimant_name. */
    claimantNameFor?: (record: ExtractedRecord) => string | undefined;
    /** The projection's paid/incurred windows (#7436) — names which side of the window a flagged line fell on. */
    policyWindows?: PolicyWindows | null;
    /** The rail's "Dates are right" has been clicked: flagged lines drop the warning tone. */
    outsidePolicyAcknowledged?: boolean;
  };
  /** Document's record_type — drives whether the SL or reimbursement chip
   * variants render in the "What happens" column. Undefined = SL behavior. */
  documentRecordType?: DocumentRecordType;
  /** When set on a reimbursement document, returns the per-claimant
   * reimbursement summary for a row. Replaces the SL forecast chip + YTD
   * column with reimbursement-specific equivalents. */
  reimbursementSummaryGetter?: (record: ExtractedRecord) => ReimbursementSummary | undefined;
  /** Per-row reimbursement forecast lookup. On the flat Reimbursements tab
   * this drives the "On file" chip + muted row treatment so the reviewer can
   * see at a glance which rows will load and which are already on file from
   * a prior upload. Safe to pass on non-reimbursement schemas — the lookup
   * just returns undefined. Records the loader will drop or collapse are
   * filtered out upstream in DocumentReviewTab — they never reach this grid
   * for reimbursement docs. AB#4455. */
  reimbursementRowGetter?: (record: ExtractedRecord) => ReimbursementRowForecast | undefined;
  /** Per-row claim_item forecast lookup. On the flat Claims tab (and the nested
   * claim-item table) this drives the "New" / "On file" chip + muted row for
   * on-file lines, using the same natural-key dedup the loader runs on approve
   * (AB#4456). Safe to pass on non-claim schemas — returns undefined. */
  claimItemRowGetter?: (record: ExtractedRecord) => ClaimItemRowForecast | undefined;
  /**
   * Rendered at the left of the search row — the parent's active filter chip
   * (#7432). Lets the filter and the search share one line instead of
   * stacking two toolbars above the table.
   */
  toolbarStart?: ReactNode;
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Review grid for extracted records, built on `CompactTable`.
 *
 * Features:
 * - Full-text search across all flattened fields
 * - Error-first sort within the current page slice
 * - Status column (ValidationErrorsCell) + optional View Source column
 * - Multi-select with batch exclude / restore via the selection toolbar
 * - Optional `expandRow` panel (driven by the parent) for rollup views
 */
export const RecordReviewGrid = ({
  records,
  targetSchema,
  columns,
  emptyMessage = "No records found matching your filters",
  onViewSource,
  sourceType = "spreadsheet",
  isExcluded,
  totalRecordCount,
  hasMoreRecords,
  onBulkExclude,
  onBulkRestore,
  expandRow,
  expandLabel,
  isExpandable,
  forecastGetter,
  isProjectionLoading,
  isFamilyDeductiblePolicy,
  claimantGrid,
  claimLinesGrid,
  documentRecordType,
  reimbursementSummaryGetter,
  reimbursementRowGetter,
  claimItemRowGetter,
  toolbarStart,
}: RecordReviewGridProps) => {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Pre-compute searchable strings once per record set
  const searchableRecords = useMemo(
    () => records.map((r) => JSON.stringify(getRecordData(r)).toLowerCase()),
    [records]
  );

  const filteredRecords = useMemo(() => {
    let result = records;
    if (search) {
      const needle = search.toLowerCase();
      result = result.filter((_r, idx) => searchableRecords[idx].includes(needle));
    }
    // Error-first sort
    return [...result].sort((a, b) => {
      const aErrors = getRecordValidationErrors(a).filter((e) => !e.schema || e.schema === targetSchema).length;
      const bErrors = getRecordValidationErrors(b).filter((e) => !e.schema || e.schema === targetSchema).length;
      if (aErrors !== bErrors) return bErrors - aErrors;
      const aWarn = getRecordTransformationErrors(a).filter((e) => !e.schema || e.schema === targetSchema).length;
      const bWarn = getRecordTransformationErrors(b).filter((e) => !e.schema || e.schema === targetSchema).length;
      return bWarn - aWarn;
    });
  }, [records, search, searchableRecords, targetSchema]);

  const rows = useMemo(() => filteredRecords.map(asRow), [filteredRecords]);

  // Map from synthetic id back to original record for bulk actions
  const rowById = useMemo(() => {
    const map = new Map<string, ExtractedRecord>();
    rows.forEach((row) => map.set(row.id, row));
    return map;
  }, [rows]);

  // Build columns: data columns + Status (always) + View Source (optional)
  const tableColumns: CompactTableColumn<ExtractedRecordRow>[] = useMemo(() => {
    // Redesigned claimant grid (#7435): SL top-row path with claimant context.
    const isClaimantGrid =
      !!forecastGetter && !!claimantGrid && documentRecordType !== DocumentRecordType.REIMBURSEMENTS;
    // Redesigned claim lines tab (artboard 2a): fixed columns, not the dynamic
    // field table.
    const isClaimLinesGrid = !!claimLinesGrid && targetSchema === TargetSchema.CLAIM_ITEM;

    const dataCols: CompactTableColumn<ExtractedRecordRow>[] = (isClaimLinesGrid ? [] : columns)
      // No Member ID column (#7435 AC1): it stays searchable, is the name's
      // hover title, and prints in the expanded panel.
      .filter((col) => !(isClaimantGrid && col.key === "member_id"))
      .map((col) => ({
        key: col.key,
        label: col.label,
        width: col.width,
        align: col.align,
        render: (row) => {
          if (isClaimantGrid && col.key === "claimant_name") {
            const memberId = getRecordData(row, TargetSchema.CLAIMANT).member_id;
            return (
              <ClaimantNameCell
                name={col.render(row)}
                memberId={memberId !== null && memberId !== undefined ? String(memberId) : undefined}
                projection={forecastGetter(row)}
                lineCount={claimantGrid?.lineCount?.(row)}
                lineCountPartial={claimantGrid?.lineCountPartial}
              />
            );
          }
          return col.render(row);
        },
      }));

    const buildErrorsList = (row: ExtractedRecord): ValidationError[] => {
      const validationErrors = getRecordValidationErrors(row)
        .filter((e) => !e.schema || e.schema === targetSchema)
        .map((e) => ({ field: e.field, message: e.message, severity: "error" })) as ValidationError[];
      const transformationErrors = getRecordTransformationErrors(row)
        .filter((e) => !e.schema || e.schema === targetSchema)
        .map((e) => ({ field: e.field, message: e.message, severity: "warning" })) as ValidationError[];
      return [...validationErrors, ...transformationErrors];
    };

    if (isClaimLinesGrid) {
      const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
      const date = (v: unknown) => (str(v) ? formatDate(str(v) as string, DATE_FORMATS.DISPLAY_LONG) : "—");
      dataCols.push(
        {
          key: "claimant_name",
          label: "Claimant Name",
          width: "minmax(140px, 1fr)",
          render: (row) => {
            const name =
              claimLinesGrid?.claimantNameFor?.(row) ??
              str(getRecordData(row, TargetSchema.CLAIMANT).claimant_name) ??
              str(getRecordData(row).claimant_name);
            return (
              <Typography variant="body2" noWrap title={name}>
                {name ?? "—"}
              </Typography>
            );
          },
        },
        {
          key: "__service_date",
          label: "Service date",
          width: "112px",
          render: (row) => {
            const d = getRecordData(row, TargetSchema.CLAIM_ITEM);
            return (
              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                {date(d.date_of_service_start ?? d.date_of_service)}
              </Typography>
            );
          },
        },
        {
          key: "paid_date",
          label: "Paid date",
          width: "112px",
          render: (row) => (
            <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
              {date(getRecordData(row, TargetSchema.CLAIM_ITEM).paid_date)}
            </Typography>
          ),
        },
        {
          key: "__procedure",
          label: "Procedure",
          width: "minmax(160px, 1.2fr)",
          render: (row) => {
            const d = getRecordData(row, TargetSchema.CLAIM_ITEM);
            const text = [str(d.procedure_code), str(d.procedure_description)].filter(Boolean).join(" ") || "—";
            return (
              <Typography variant="body2" noWrap title={text}>
                {text}
              </Typography>
            );
          },
        },
        {
          key: "paid_amount",
          label: "Paid",
          width: "110px",
          align: "right",
          render: (row) => {
            const paid = parseMoneyCell(getRecordData(row, TargetSchema.CLAIM_ITEM).paid_amount);
            return (
              <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {paid === null ? "—" : formatUSD(paid)}
              </Typography>
            );
          },
        }
      );
    }

    if (forecastGetter && isFamilyDeductiblePolicy && !isClaimantGrid) {
      // Family column — only on family-deductible policies. On individual
      // policies there's no family unit to label, so the column is suppressed
      // entirely instead of rendering blank cells. Role-aware labels:
      // "Subscriber" on the EE row, "Dependent · {EE}" or "Spouse · {EE}"
      // on dep/spouse rows; "Dependent · pending" / "Spouse · pending" when
      // the row is flagged is_pending_family_link (no EE resolved yet, in
      // file or DB). The "What happens" forecast and YTD columns sit
      // outside this branch — they apply on every top-row schema regardless
      // of deductible type.
      dataCols.push({
        key: "__family",
        label: "Family",
        width: "minmax(160px, 200px)",
        render: (row) => {
          const proj = forecastGetter(row);
          const anchorName = proj?.family_subscriber_name;
          const isPending = !!proj?.is_pending_family_link;
          if (!anchorName && !isPending) return null;

          const rel = proj?.claimant_relationship;
          let label: string;
          let showAnchor = false;
          switch (rel) {
            case "self":
              label = "Subscriber";
              break;
            case "spouse":
              label = "Spouse";
              showAnchor = true;
              break;
            case "dependent":
              label = "Dependent";
              showAnchor = true;
              break;
            default:
              // Rare: relationship unknown but family resolved anyway.
              // Fall back to generic Family label with anchor name.
              label = "Family";
              showAnchor = true;
          }

          // Pending state: row is on a family-ded policy with no resolvable
          // subscriber yet. Render the relationship label + "pending" suffix
          // in a warning tint to mirror the ForecastChip's pending state, so
          // both columns telegraph the same signal.
          if (isPending) {
            return (
              <Stack direction="row" alignItems="center" gap={0.75} sx={{ overflow: "hidden" }}>
                <GroupIcon sx={{ fontSize: "1rem", color: "warning.main", flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: "warning.dark",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    flexShrink: 0,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "warning.dark",
                    fontStyle: "italic",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  · pending
                </Typography>
              </Stack>
            );
          }

          return (
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ overflow: "hidden" }}>
              <GroupIcon sx={{ fontSize: "1rem", color: "text.disabled", flexShrink: 0 }} />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  flexShrink: 0,
                }}
              >
                {label}
              </Typography>
              {showAnchor && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  · {anchorName}
                </Typography>
              )}
            </Stack>
          );
        },
      });
    }

    // The forecast surface differs by document type. Reimbursement docs
    // forecast carrier-payout creation + SL-claim linkage; everything else
    // (stoploss claims, etc.) forecasts breach + SL-claim creation. Both
    // branches render on top-row schemas only — non-top-row schemas keep the
    // legacy Status column.
    if (reimbursementSummaryGetter && documentRecordType === DocumentRecordType.REIMBURSEMENTS) {
      dataCols.push({
        key: "__forecast",
        label: "What Happens",
        width: "240px",
        render: (row) => (
          <ReimbursementForecastChip
            summary={reimbursementSummaryGetter(row)}
            errors={buildErrorsList(row)}
            isLoading={!!isProjectionLoading}
          />
        ),
      });
      dataCols.push({
        key: "__reimbursements",
        label: "Reimbursements",
        width: "240px",
        render: (row) => (
          <ReimbursementsForecastCell summary={reimbursementSummaryGetter(row)} isLoading={!!isProjectionLoading} />
        ),
      });
    } else if (forecastGetter) {
      // SL-claim forecast — semantic chip of what approval will do. Falls
      // back to the errors display when there are validation/transformation
      // issues. Renders on every top-row schema regardless of deductible
      // type — meaningful for individual-deductible policies too (creates SL
      // claim, updates SL claim, no updates, etc.).
      if (isClaimantGrid) {
        // #7435 (artboard 2a): YTD · Deductible · On approve. One YTD figure
        // (reported, else on-file + claim lines), the deductible this row is
        // measured against, and the forecast chip with an over-deductible
        // note only on stop-loss rows.
        dataCols.push({
          key: "__ytd",
          label: <YTDHeader asOfLabel={claimantGrid?.asOfLabel} />,
          width: "150px",
          render: (row) => (
            <YTDForecastCell
              projection={forecastGetter(row)}
              isLoading={!!isProjectionLoading}
              mismatch={claimantGrid?.ytdMismatchFor?.(row)}
            />
          ),
        });
        dataCols.push({
          key: "__deductible",
          label: <DeductibleHeader policyLabel={claimantGrid?.policyLabel} />,
          width: "130px",
          render: (row) => <DeductibleCell projection={forecastGetter(row)} isLoading={!!isProjectionLoading} />,
        });
        dataCols.push({
          key: "__forecast",
          label: "On approve",
          width: "220px",
          render: (row) => {
            const proj = forecastGetter(row);
            // Sub-line only when it carries a decision (artboard 2a): the
            // unlinked-member consequence, or the over-deductible amount on a
            // stop-loss row.
            const note = isProjectionLoading
              ? null
              : proj?.is_pending_family_link
                ? { text: "No stop-loss claim until linked", tone: "warning.dark" }
                : (() => {
                    const over = overDeductibleNote(proj);
                    return over ? { text: over, tone: "text.secondary" } : null;
                  })();
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, alignItems: "flex-start" }}>
                <ForecastChip
                  projection={proj}
                  errors={buildErrorsList(row)}
                  isLoading={!!isProjectionLoading}
                  incomingLines={claimantGrid?.incomingLinesFor?.(row)}
                />
                {note && (
                  <Typography variant="caption" sx={{ lineHeight: 1.3, color: note.tone, whiteSpace: "nowrap" }}>
                    {note.text}
                  </Typography>
                )}
              </Box>
            );
          },
        });
      } else {
        dataCols.push({
          key: "__forecast",
          label: "What Happens",
          width: "200px",
          render: (row) => (
            <ForecastChip
              projection={forecastGetter(row)}
              errors={buildErrorsList(row)}
              isLoading={!!isProjectionLoading}
            />
          ),
        });
        dataCols.push({
          key: "__ytd",
          label: "YTD",
          // Single figure since #7435; the before→after pair needed 280px.
          width: "150px",
          render: (row) => <YTDForecastCell projection={forecastGetter(row)} isLoading={!!isProjectionLoading} />,
        });
      }
    } else if (targetSchema === TargetSchema.REIMBURSEMENT) {
      // Reimbursement tab (flat, non-top schema) — "What happens" column
      // pairs with the per-row state chip (Create / Unlinked / Needs link
      // review / On file) so the row-level disposition matches the per-
      // claimant rollup vocabulary on the Claimants tab. Errors still take
      // precedence — they're the loudest signal.
      //
      // Anchored to the LEFT (unshift, not push) so the marker is visible
      // without horizontal scroll on wide reimbursement tables. View Source
      // unshifts after this so the final left-to-right order is
      // [View Source, What happens, ...data cols].
      dataCols.unshift({
        key: "__status",
        label: "What Happens",
        width: "144px",
        render: (row) => {
          const errors = buildErrorsList(row);
          if (errors.length > 0) return <ValidationErrorsCell errors={errors} />;
          return <ReimbursementRowStateChip forecast={reimbursementRowGetter?.(row)} />;
        },
      });
    } else if (isClaimLinesGrid) {
      // On approve goes after Source below; nothing to unshift here.
    } else if (targetSchema === TargetSchema.CLAIM_ITEM && records.some((r) => claimItemRowGetter?.(r) !== undefined)) {
      // Claims tab (flat, non-top schema) — "What Happens" column pairs the
      // per-row state chip (New / On file) with errors-first precedence,
      // mirroring the Reimbursement tab. The chip is backed by the same
      // natural-key dedup the loader runs on approve (AB#4456), so it's an
      // honest per-line preview. Anchored left (unshift). AB#4551.
      //
      // Gated on the projection actually having a forecast for one of these
      // records: a reimbursement doc that happens to map claim_item fields gets
      // a Claim Items tab but no claim_item_forecasts (it takes the reimbursement
      // projection branch), and the request can fail/still-be-loading — in all
      // those cases the column would render permanently blank. (AB#4456 review.)
      dataCols.unshift({
        key: "__status",
        label: "What Happens",
        width: "144px",
        render: (row) => {
          const errors = buildErrorsList(row);
          if (errors.length > 0) return <ValidationErrorsCell errors={errors} />;
          return <ClaimItemRowStateChip forecast={claimItemRowGetter?.(row)} />;
        },
      });
    } else if (targetSchema === TargetSchema.STOPLOSS_CLAIM) {
      // Stop-Loss Claims tab (flat, non-top schema) — errors-only per-row
      // column. SL claims are derived per-claimant (the Claimants tab already
      // shows Creates/Updates SL claim), so there is no per-row forecast to
      // mirror here; this restores the per-row error visibility lost when the
      // Issues sidebar was removed (AB#4480) so a reviewer can see WHICH row
      // carries an error, not just that the tab has one. Empty when clean.
      // Anchored left (unshift). AB#4551.
      dataCols.unshift({
        key: "__status",
        label: "Status",
        width: "144px",
        render: (row) => {
          const errors = buildErrorsList(row);
          if (errors.length > 0) return <ValidationErrorsCell errors={errors} />;
          return null;
        },
      });
    }

    // On approve precedes Source, as on the Claimants tab.
    if (isClaimLinesGrid) {
      dataCols.push({
        key: "__outcome",
        label: "On approve",
        width: "minmax(120px, 200px)",
        render: (row) => (
          <ClaimLineOutcomeCell
            forecast={claimItemRowGetter?.(row)}
            errors={buildErrorsList(row)}
            excluded={!!isExcluded?.(row)}
            outside={outsidePolicyLineFor(row, claimItemRowGetter?.(row), claimLinesGrid?.policyWindows)}
            outsideAcknowledged={!!claimLinesGrid?.outsidePolicyAcknowledged}
          />
        ),
      });
    }

    if (onViewSource && (isClaimantGrid || isClaimLinesGrid)) {
      // Claimant grid (#7435, artboard 2a): Source is the LAST column and
      // reads "Row 66" / "Page 3" with the source-type icon — the same link
      // the expanded claim-lines panel uses. The left-anchored icon column
      // below exists for wide dynamic tables that scroll horizontally; this
      // grid has five fixed columns and never does.
      dataCols.push({
        key: "__source",
        label: "Source",
        width: "110px",
        align: "right",
        render: (row) => (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={(e) => {
              e.stopPropagation();
              onViewSource(row);
            }}
            aria-label={`View ${sourceLabelFor(row, sourceType).toLowerCase()} in the source file`}
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}
          >
            {sourceLabelFor(row, sourceType)}
            {sourceType === "pdf" ? (
              <PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} />
            ) : (
              <TableChartOutlinedIcon sx={{ fontSize: 16 }} />
            )}
          </Link>
        ),
      });
    } else if (onViewSource) {
      // View-source button is anchored to the LEFT of the row so it stays
      // visible regardless of horizontal scroll. Wide tables can push the
      // rightmost cells off-screen; pinning the source-jump action to a
      // fixed-position leading column means a reviewer who scrolls horizontally
      // through 30 data columns can always click back to the source PDF /
      // spreadsheet without scrolling back.
      dataCols.unshift({
        key: "__viewSource",
        label: "",
        width: "44px",
        align: "center",
        render: (row) => (
          <Tooltip title={sourceType === "pdf" ? "View in PDF" : "View in spreadsheet"} placement="right">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onViewSource(row);
              }}
              sx={{ color: "text.secondary" }}
            >
              {sourceType === "pdf" ? (
                <PictureAsPdfOutlinedIcon fontSize="small" />
              ) : (
                <TableChartOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        ),
      });
    }

    return dataCols;
  }, [
    columns,
    targetSchema,
    onViewSource,
    claimantGrid,
    claimLinesGrid,
    isExcluded,
    sourceType,
    forecastGetter,
    isProjectionLoading,
    isFamilyDeductiblePolicy,
    documentRecordType,
    reimbursementSummaryGetter,
    reimbursementRowGetter,
    claimItemRowGetter,
  ]);

  // Wrap expandRow / expandLabel to accept ExtractedRecordRow
  const expandRowForRow = useMemo(
    () => (expandRow ? (row: ExtractedRecordRow) => expandRow(row) : undefined),
    [expandRow]
  );
  const expandLabelForRow = useMemo(
    () => (expandLabel ? (row: ExtractedRecordRow) => expandLabel(row) : undefined),
    [expandLabel]
  );
  const isExpandableForRow = useMemo(
    () => (isExpandable ? (row: ExtractedRecordRow) => isExpandable(row) : undefined),
    [isExpandable]
  );

  // Selection toolbar — batch exclude / restore. Decides which action to show
  // based on whether any of the selected rows are currently included.
  const selectedRecords = useMemo(
    () => selectedIds.map((id) => rowById.get(id)).filter((r): r is ExtractedRecord => !!r),
    [selectedIds, rowById]
  );

  const selectedCount = selectedRecords.length;
  const someIncluded = isExcluded ? selectedRecords.some((r) => !isExcluded(r)) : selectedRecords.length > 0;

  const selectionToolbar =
    selectedCount > 0 && (onBulkExclude || onBulkRestore) ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          bgcolor: "action.selected",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {selectedCount} selected
          </Typography>
          {/* Cascade scope disclosure (#6755, fix tracked as #6848). Excluding
              a claimant cascades through linked records — but only the loaded
              ones, so on a paged document the exclusion can look complete while
              linked rows on unfetched pages still load on approve. Same
              stated-scope treatment as the search helper text. */}
          {/* No count quoted on purpose: `records` here is the current schema's
              bucket, while the cascade runs across ALL loaded records. */}
          {hasMoreRecords && someIncluded && (
            <Typography variant="caption" color="text.secondary">
              Excluding cascades through loaded records only. Linked records on pages not yet loaded are not excluded —
              load more to widen the cascade.
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {someIncluded && onBulkExclude && (
            <Button
              size="small"
              startIcon={<ExcludeIcon fontSize="small" />}
              onClick={() => {
                onBulkExclude(selectedRecords);
                setSelectedIds([]);
              }}
            >
              Exclude
            </Button>
          )}
          {!someIncluded && onBulkRestore && (
            <Button
              size="small"
              color="success"
              startIcon={<RestoreIcon fontSize="small" />}
              onClick={() => {
                onBulkRestore(selectedRecords);
                setSelectedIds([]);
              }}
            >
              Restore
            </Button>
          )}
        </Box>
      </Box>
    ) : null;

  const isRowSelectable = onBulkExclude || onBulkRestore ? () => true : undefined;

  return (
    // minWidth: 0 prevents this flex/grid child from sizing to its content
    // width — without it, the inner table can blow out the container instead
    // of triggering its own horizontal scroll.
    <Box sx={{ minWidth: 0 }}>
      {/* One toolbar row: the parent's filter chip on the left, a compact
          search on the right. The search was a full-width bar with a
          scope-stating placeholder; the scope now lives in the helper text
          that appears only while searching a paged document. */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, mb: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexWrap: "wrap", pt: 0.25 }}>
          {toolbarStart}
        </Box>
        <TextField
          placeholder="Search"
          aria-label="Search records"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          // The theme's default is the filled variant, which reserves room for
          // a floating label: pinned to 32px the text and icon sat low against
          // a flat underline. hiddenLabel drops that padding; the underline
          // goes so the box is a rounded pill, and the content centres.
          hiddenLabel
          sx={{
            width: 240,
            flexShrink: 0,
            "& .MuiInputBase-root": { height: 32, fontSize: 13, borderRadius: 1.5, alignItems: "center" },
            "& .MuiInputBase-input": { py: 0, height: "100%" },
            "& .MuiInputAdornment-root": { mt: "0 !important" },
          }}
          helperText={
            hasMoreRecords && search
              ? `Searched the ${records.length.toLocaleString()} loaded records${
                  totalRecordCount ? ` of ${totalRecordCount.toLocaleString()}` : ""
                }. Load more to widen the search.`
              : undefined
          }
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <CompactTable<ExtractedRecordRow>
        data={rows}
        columns={tableColumns}
        rowsPerPage={25}
        emptyMessage={emptyMessage}
        selectable={!!(onBulkExclude || onBulkRestore)}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        selectionToolbar={selectionToolbar}
        isSelectable={isRowSelectable}
        expandRow={expandRowForRow}
        expandLabel={expandLabelForRow}
        isExpandable={isExpandableForRow}
        getRowSx={(row) => {
          // Excluded styling wins — strike-through + bg + opacity is the louder
          // signal and shouldn't be diluted by overlaying duplicate-row muting.
          if (isExcluded?.(row)) {
            return {
              opacity: 0.55,
              bgcolor: "action.disabledBackground",
              "& .MuiTypography-root": { textDecoration: "line-through" },
            };
          }
          // Reimbursement row that already exists in the system from a prior
          // load. Approving skips insertion; muting tells the reviewer at a
          // glance which rows are no-ops without scrolling to the Status chip.
          if (reimbursementRowGetter?.(row)?.is_duplicate_on_file) {
            return { opacity: 0.55 };
          }
          // Claim line already on file (Operation "update"). Approving refreshes
          // it in place rather than adding a new line — mute it so the reviewer's
          // eye goes to the New rows. Scoped to the flat Claims tab ONLY: on an
          // itemized doc a claimant row and one of its claim lines can share an
          // extracted record, so applying this on the Claimants tab would mute a
          // claimant row whenever a single underlying line is on file — wrong and
          // confusing when the claimant has a mix of new and on-file lines. The
          // nested claim-item table does its own (correctly scoped) muting. AB#4456.
          if (targetSchema === TargetSchema.CLAIM_ITEM) {
            const claimItemRow = claimItemRowGetter?.(row);
            if (claimItemRow?.operation === "update" || claimItemRow?.is_duplicate_on_file) {
              return { opacity: 0.55 };
            }
          }
          return {};
        }}
      />
    </Box>
  );
};
